// The sign-in card's fields (legacy LoginView, PasswordReset): an outlined password field whose eye icon shows or
// hides what was typed, and the error line the auth pages share. Log in's and Reset's eye is the primary green
// (`input-auth-icon`); the invitation's is Quasar's grey.
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { TextField } from '@radish/ui-web';
import { MATERIAL_VISIBILITY, MATERIAL_VISIBILITY_OFF, MaterialIcon } from '../../shell/material-icons.tsx';

interface PasswordFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly error?: string | undefined;
  readonly autoComplete: 'current-password' | 'new-password';
  /** The grey 20 px eye (the invitation, and Edit User) instead of the sign-in pages' green one. */
  readonly mutedEye?: boolean;
}

export function PasswordField({ label, value, onValueChange, error, autoComplete, mutedEye = false }: PasswordFieldProps) {
  const [shown, setShown] = useState(false);
  return (
    <TextField
      label={label}
      value={value}
      onValueChange={onValueChange}
      error={error}
      type={shown ? 'text' : 'password'}
      autoComplete={autoComplete}
      append={
        <button type="button" onClick={() => setShown(!shown)} aria-label={shown ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`} className={mutedEye ? 'flex cursor-pointer' : 'text-primary'}>
          {/* mutedEye: legacy's #append q-icon `visibility` / `visibility_off`, 24 px in the field's marginal ink (B1134). */}
          {mutedEye
            ? <MaterialIcon path={shown ? MATERIAL_VISIBILITY : MATERIAL_VISIBILITY_OFF} size={24} />
            : shown ? <Eye size={24} aria-hidden /> : <EyeOff size={24} aria-hidden />}
        </button>
      }
    />
  );
}

export function FormProblem({ message }: { readonly message: string | null }) {
  if (message === null) return null;
  return (
    <p role="alert" className="rounded-q bg-negative/10 px-3 py-2 text-[14px] text-negative" data-testid="auth-problem">
      {message}
    </p>
  );
}

/**
 * The sign-in pages' 400 px card (legacy `q-card q-pa-lg`, 24 px padding, 8 px corners): Log in's is flat with a 1 px
 * border, the others keep Quasar's default card shadow.
 */
export function AuthCard({ bordered = false, testId, children }: { readonly bordered?: boolean; readonly testId?: string; readonly children: React.ReactNode }) {
  const edge = bordered ? 'border border-line' : 'shadow-[0_1px_5px_rgba(0,0,0,0.2),0_2px_2px_rgba(0,0,0,0.14),0_3px_1px_-2px_rgba(0,0,0,0.12)]';
  return (
    <div data-testid={testId} className={`w-[400px] max-w-full rounded-q bg-card p-6 ${edge}`}>
      {children}
    </div>
  );
}

/** Log in's heading (legacy LoginView): "WELCOME!" in 14 px bold capitals over a 34 px semibold title, centred, 24 px above the form. */
export function AuthHeading({ eyebrow, title, children }: { readonly eyebrow: string; readonly title: string; readonly children?: React.ReactNode }) {
  return (
    <div className="mb-6 text-center">
      <div className="text-[14px] font-bold text-text-2 uppercase">{eyebrow}</div>
      <h1 className="text-[34px] leading-10 font-semibold text-text-1">{title}</h1>
      {children}
    </div>
  );
}

/** Forgot password's and Reset's title (legacy `text-h4 text-weight-bold`): 34 px bold on a 40 px line, left, in the body grey. */
export function AuthTitle({ children, className = '' }: { readonly children: React.ReactNode; readonly className?: string }) {
  return <h1 className={`text-[34px] leading-10 font-bold text-text-2 ${className}`}>{children}</h1>;
}

/** Legacy BaseAlert type="error": white text on the negative red, 7 px corners, 8 px padding, Material's filled error circle at 28 px. */
export function ErrorAlert({ children, testId }: { readonly children: React.ReactNode; readonly testId?: string }) {
  return (
    <div role="alert" data-testid={testId} className="flex items-start gap-2 rounded-[7px] bg-negative p-2 text-[14px] leading-[21px] text-white/87">
      <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden className="shrink-0 fill-white">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
      </svg>
      <span className="ml-2 min-w-0">{children}</span>
    </div>
  );
}

/** Legacy's blue, bold text link under a card's button ("Return to Log in"). */
export const AUTH_LINK = 'block text-center text-[14px] leading-[21px] font-bold text-secondary hover:underline';
